import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApplicationById } from '../../api/services/ApplicationService';
import { Card, Button, Row, Col, Badge, Spinner, Tab, Tabs, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaUser, FaMapMarkerAlt, FaGraduationCap, FaFileInvoiceDollar, FaCalendarAlt, FaIdCard } from 'react-icons/fa';
import styled from 'styled-components';

const SingleApplicationView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [application, setApplication] = useState(null);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('personal');

    useEffect(() => {
        const fetchApplication = async () => {
            try {
                setLoading(true);

                const response = await getApplicationById(id);

                if (response?.application) {
                    setApplication(response.application);
                    setPayments(response.payments || []);
                } else {
                    throw new Error("Invalid response structure");
                }

            } catch (err) {
                console.error(err);
                setError("Failed to load application details");
            } finally {
                setLoading(false);
            }
        };

        fetchApplication();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const renderStatusBadge = (status) => {
        let variant = 'secondary';
        if (status === 'approved') variant = 'success';
        if (status === 'pending') variant = 'warning';
        if (status === 'rejected') variant = 'danger';

        return <StatusBadge bg={variant}>{status}</StatusBadge>;
    };

    const renderPaymentStatus = (status) => {
        let variant = 'warning';
        if (status === 'SUCCESS') variant = 'success';
        if (status === 'FAILED' || status === 'CANCELLED') variant = 'danger';

        return <Badge bg={variant}>{status?.toLowerCase() || 'unknown'}</Badge>;
    };

    if (loading) {
        return (
            <LoaderContainer>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </LoaderContainer>
        );
    }

    if (error) {
        return (
            <AlertContainer variant="danger" className="text-center">
                {error}
            </AlertContainer>
        );
    }

    if (!application) {
        return (
            <AlertContainer variant="warning" className="text-center">
                Application not found
            </AlertContainer>
        );
    }

    // Safely extract name parts
    const fullName = [application.firstName, application.middleName, application.lastName]
        .filter(Boolean)
        .join(' ') || application.name || 'N/A';

    return (
        <div className="application-view-container">
            <header className="content-header mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                    <h1 className="mb-0 h4">Application Details</h1>
                    <BackButton variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FaArrowLeft className="me-2" />
                        Back
                    </BackButton>
                </div>

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        <ApplicationNumber>Application No: <strong>{application.applicationNo}</strong></ApplicationNumber>
                        {application.paymentStatus && (
                            <Badge bg={application.paymentStatus === 'PAID' ? 'success' : 'warning'}>
                                {application.paymentStatus}
                            </Badge>
                        )}
                    </div>
                </div>
            </header>

            <TabsContainer>
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="custom-tabs mb-4"
                    id="application-tabs"
                >
                    {/* Personal Tab */}
                    <Tab eventKey="personal" title={<><FaUser className="me-2" />Personal</>}>
                        <Row>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaUser className="me-2" />Basic Information</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Full Name:</LabelCol>
                                            <ValueCol sm={8}>{fullName}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Date of Birth:</LabelCol>
                                            <ValueCol sm={8}>{application.dob || '-'}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Gender:</LabelCol>
                                            <ValueCol sm={8}>{application.gender || '-'}</ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>

                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaMapMarkerAlt className="me-2" />Contact Information</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Email:</LabelCol>
                                            <ValueCol sm={8}>{application.email}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Phone:</LabelCol>
                                            <ValueCol sm={8}>{application.contact}</ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                        </Row>

                        <Row>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaMapMarkerAlt className="me-2" />Address</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Address:</LabelCol>
                                            <ValueCol sm={8}>{application.address || '-'}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>City:</LabelCol>
                                            <ValueCol sm={8}>{application.city || '-'}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>District:</LabelCol>
                                            <ValueCol sm={8}>{application.district || '-'}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Postal Code:</LabelCol>
                                            <ValueCol sm={8}>{application.pinCode || '-'}</ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                        </Row>
                    </Tab>

                    {/* Education Tab */}
                    <Tab eventKey="education" title={<><FaGraduationCap className="me-2" />Education</>}>
                        <Row>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaGraduationCap className="me-2" />Education Details</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Level:</LabelCol>
                                            <ValueCol sm={8}>{application.level || '-'}</ValueCol>
                                        </DetailRow>

                                        {/* SCHOOL */}
                                        {application.level === 'school' && application.schoolDetails && (
                                            <>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Grade:</LabelCol>
                                                    <ValueCol sm={8}>{application.schoolDetails.grade}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>School:</LabelCol>
                                                    <ValueCol sm={8}>{application.schoolDetails.schoolName}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Place:</LabelCol>
                                                    <ValueCol sm={8}>{application.schoolDetails.place}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Board:</LabelCol>
                                                    <ValueCol sm={8}>{application.schoolDetails.board}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Fee:</LabelCol>
                                                    <ValueCol sm={8}>₹{application.schoolDetails.fee}</ValueCol>
                                                </DetailRow>
                                            </>
                                        )}

                                        {/* UG */}
                                        {application.level === 'ug' && application.ugDetails && (
                                            <>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Year:</LabelCol>
                                                    <ValueCol sm={8}>{application.ugDetails.year}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>College:</LabelCol>
                                                    <ValueCol sm={8}>{application.ugDetails.collegeName}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Place:</LabelCol>
                                                    <ValueCol sm={8}>{application.ugDetails.place}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>University:</LabelCol>
                                                    <ValueCol sm={8}>{application.ugDetails.affiliatedUniversity}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Program:</LabelCol>
                                                    <ValueCol sm={8}>{application.ugDetails.programDepartment || '-'}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Type:</LabelCol>
                                                    <ValueCol sm={8}>{application.ugDetails.collegeType}</ValueCol>
                                                </DetailRow>
                                            </>
                                        )}

                                        {/* PG */}
                                        {application.level === 'pg' && application.pgDetails && (
                                            <>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Year:</LabelCol>
                                                    <ValueCol sm={8}>{application.pgDetails.year}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>College:</LabelCol>
                                                    <ValueCol sm={8}>{application.pgDetails.collegeName}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Place:</LabelCol>
                                                    <ValueCol sm={8}>{application.pgDetails.place}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>University:</LabelCol>
                                                    <ValueCol sm={8}>{application.pgDetails.affiliatedUniversity}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Program:</LabelCol>
                                                    <ValueCol sm={8}>{application.pgDetails.programDepartment || '-'}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Mode:</LabelCol>
                                                    <ValueCol sm={8}>{application.pgDetails.mode}</ValueCol>
                                                </DetailRow>
                                            </>
                                        )}

                                        {/* Individual */}
                                        {application.level === 'individual' && application.individualDetails && (
                                            <>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Occupation:</LabelCol>
                                                    <ValueCol sm={8}>{application.individualDetails.occupation}</ValueCol>
                                                </DetailRow>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Organization:</LabelCol>
                                                    <ValueCol sm={8}>{application.individualDetails.organizationName}</ValueCol>
                                                </DetailRow>
                                            </>
                                        )}

                                        {/* Exam Selections */}
                                        {Array.isArray(application.examSelections) && application.examSelections.length > 0 && (
                                            <>
                                                <hr className="my-3" />
                                                <h6 className="mt-2 mb-3">Exam Selections</h6>
                                                {application.examSelections.map((item, index) => (
                                                    <DetailRow key={index}>
                                                        <LabelCol sm={4}>Exam {index + 1}:</LabelCol>
                                                        <ValueCol sm={8}>
                                                            {item.specialization} | {item.examLevel} | ₹{item.fee}
                                                        </ValueCol>
                                                    </DetailRow>
                                                ))}
                                                <DetailRow>
                                                    <LabelCol sm={4}><strong>Total Amount:</strong></LabelCol>
                                                    <ValueCol sm={8}><strong>₹{application.totalAmount}</strong></ValueCol>
                                                </DetailRow>
                                            </>
                                        )}
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                        </Row>
                    </Tab>

                    {/* Payment Tab */}
                    <Tab
                        eventKey="payment"
                        title={
                            <>
                                <FaFileInvoiceDollar className="me-2" />
                                Payment
                                {payments.length > 0 && (
                                    <Badge bg="success" className="ms-2">
                                        {payments.length}
                                    </Badge>
                                )}
                            </>
                        }
                    >
                        {payments.length > 0 ? (
                            payments.map((payment, idx) => (
                                <Row key={payment.orderId || idx} className="mb-4">
                                    <Col lg={6}>
                                        <DetailCard>
                                            <Card.Header>
                                                <h5>
                                                    <FaFileInvoiceDollar className="me-2" />
                                                    Payment #{idx + 1}
                                                    {idx === 0 && (
                                                        <Badge bg="success" className="ms-2">
                                                            Latest
                                                        </Badge>
                                                    )}
                                                </h5>
                                            </Card.Header>

                                            <Card.Body>
                                                <DetailRow>
                                                    <LabelCol sm={4}>Order ID:</LabelCol>
                                                    <ValueCol sm={8}>{payment.orderId || "-"}</ValueCol>
                                                </DetailRow>

                                                <DetailRow>
                                                    <LabelCol sm={4}>Status:</LabelCol>
                                                    <ValueCol sm={8}>
                                                        {renderPaymentStatus(payment.status)}
                                                    </ValueCol>
                                                </DetailRow>

                                                <DetailRow>
                                                    <LabelCol sm={4}>Amount:</LabelCol>
                                                    <ValueCol sm={8}>₹{payment.amount}</ValueCol>
                                                </DetailRow>

                                                <DetailRow>
                                                    <LabelCol sm={4}>Payment Method:</LabelCol>
                                                    <ValueCol sm={8}>
                                                        {payment.paymentMethod || "Not specified"}
                                                    </ValueCol>
                                                </DetailRow>

                                                <DetailRow>
                                                    <LabelCol sm={4}>Paid At:</LabelCol>
                                                    <ValueCol sm={8}>
                                                        {formatDate(payment.paidAt)}
                                                    </ValueCol>
                                                </DetailRow>

                                                <DetailRow>
                                                    <LabelCol sm={4}>Attempted At:</LabelCol>
                                                    <ValueCol sm={8}>
                                                        {formatDate(payment.attemptedAt)}
                                                    </ValueCol>
                                                </DetailRow>
                                            </Card.Body>
                                        </DetailCard>
                                    </Col>
                                </Row>
                            ))
                        ) : (
                            <Alert variant="info" className="text-center py-3">
                                No payment records found.
                            </Alert>
                        )}
                    </Tab>

                    {/* Metadata Tab */}
                    <Tab eventKey="metadata" title={<><FaIdCard className="me-2" />Metadata</>}>

                        <Row>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaCalendarAlt className="me-2" />Timeline</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Created:</LabelCol>
                                            <ValueCol sm={8}>{formatDate(application.createdAt)}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Updated:</LabelCol>
                                            <ValueCol sm={8}>{formatDate(application.updatedAt)}</ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>

                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaIdCard className="me-2" />System Info</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Application ID:</LabelCol>
                                            <ValueCol sm={8} className="text-monospace">{application._id}</ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                        </Row>
                    </Tab>
                </Tabs>
            </TabsContainer>
        </div>
    );
};

// Styled Components
const BackButton = styled(Button)`
  background-color: #fff;
  color: #18697C;
  border-color: #18697C;
  &:hover {
    background-color: #f8f9fa;
    color: #143e47;
    border-color: #143e47;
  }
`;

const ApplicationNumber = styled.span`
  font-size: 1.1rem;
  margin-right: 1rem;
  color: #495057;
`;

const TabsContainer = styled.div`
  .custom-tabs {
    .nav-link {
      color: #495057;
      border: none;
      padding: 0.75rem 1.25rem;
      font-weight: 500;
      
      &:hover:not(.active) {
        color: #18697C;
        background-color: rgba(24, 105, 124, 0.05);
      }
      
      &.active {
        color: #18697C;
        border-bottom: 3px solid #18697C;
        background-color: transparent;
      }
    }
  }
`;

const DetailCard = styled(Card)`
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  overflow: hidden;
  
  .card-header {
    background-color: #18697C;
    color: white;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    
    h5 {
      margin: 0;
      font-size: 1.1rem;
      display: flex;
      align-items: center;
    }
  }
  
  .card-body {
    padding: 1.5rem;
  }
`;

const DetailRow = styled(Row)`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const LabelCol = styled(Col)`
  font-weight: 600;
  color: #495057;
`;

const ValueCol = styled(Col)`
  color: #212529;
  word-break: break-word;
`;

const StatusBadge = styled(Badge)`
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  text-transform: capitalize;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
`;

const AlertContainer = styled(Alert)`
  max-width: 600px;
  margin: 2rem auto;
  border-radius: 8px;
`;

export default SingleApplicationView;